import { useState, useEffect, useMemo } from "react";
import {
  styled,
  Theme,
  CSSObject,
  createTheme,
  ThemeProvider,
} from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import BusinessIcon from "@mui/icons-material/Business";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import menuItems from "./menuItems";
import theme from "../theme/theme";
import Badge from "@mui/material/Badge";
import MailIcon from "@mui/icons-material/Mail";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccountCircle from "@mui/icons-material/AccountCircle";
import PolicyIcon from "@mui/icons-material/Policy";
import SettingsIcon from "@mui/icons-material/Settings";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswerOutlined';
import MoreIcon from "@mui/icons-material/MoreVert";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate, useLocation, Link } from "react-router-dom";
import SnackBar from "./snackBar";
import { RootState, useAppSelector, useAppDispatch } from "../../store/store";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { getUserData } from "../../store/thunk/authThunk";
import { GoGear } from "react-icons/go";
import { BiBell } from "react-icons/bi";
import ThemeSwitch from "../themeSwitch";
import { manageTheme } from "../../store/slices/themeSlice";
import { useSelector } from "react-redux";
import SidebarItem from "./SidebarItem";
import SidebarItemCollapse from "./SidebarItemCollapse";
import { sendGAEvent } from "../../store/thunk/AddGAEvent";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  getUserNotificationsThunk,
  getUserNotificationsWithReadUnreadThunk,
  updateAllNotificationStatusThunk,
  updateNotificationStatusThunk,
} from "../../store/thunk/userNotificationsThunk";
import {
  Avatar,
  Button,
  CircularProgress,
  Grid,
  ListItemText,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import ContactsSupport from "./Modal/ContactsSupport";
import { sendContactSupportThunk } from "../../store/thunk/tenantThunk";
import DownloadAppComponent from "./Modal/DownloadApp";
import TermsAndCondition from "./Modal/TermsAndCondition";
import PrivacyPolicy from "./Modal/privacyPolicy";
import PublicMenu from "./publicMenu";
import {
  dynamicRoutesWhichIsNotMenu,
  routeWhichIsNotInMenu,
} from "../../constants";
import {
  setLoader as setGlobalLoader,
  setInitialLogin,
  setRouteHistory,
} from "../../store/slices/authSlice";
import {
  setDashboardInterval,
  setDetailInterval,
  setDetailPageAPIInterval,
  setSummaryInterval,
  setRelatedSubstanceInterval,
} from "../../store/slices/adminSlice";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import NotificationItems from "./notificationItems";
import __ from "underscore";
import IEQ from "../ieqGpt";

import { startTracking, stopTracking } from "../../store/slices/timeTrackerSlice";
import { updateUserHistoryData } from "../../store/thunk/tenantReport";
import TimerIcon from '@mui/icons-material/Timer';
import { isRouteInList } from "../../config/util";
import { Auth } from "aws-amplify";

const drawerWidth = 224;
const drawerRightSideWidth = 700;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const openedMixinRight = (theme: Theme): CSSObject => ({
  width: drawerRightSideWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const DownloadDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerRightSideWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixinRight(theme),
    "& .MuiDrawer-paper": openedMixinRight(theme),
  }),
}));

const usePageTracking = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  useEffect(() => {
    dispatch(stopTracking());
    dispatch(startTracking(location.pathname));
  }, [dispatch, location]);
};

export default function MiniDrawer(props: any) {
  usePageTracking();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [openSwipe, setOpenSwipe] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuList, setMenuList] = useState<any>([]);
  const [page, setPage] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [termsConditionOpen, setTermsConditionOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const { ssoenabled } = useAppSelector((state) => state.commonSlice);
  const [isNormalNotificationCall, setIsNormalNotificationCall] =
    useState(false); // Flag to indicate if the API call is made via interval
  const [notificationType, setNotificationType] = useState<string>(
    localStorage.getItem("notificationType") || "all"
  );
  const [showIeqGpt, setShowIeqGpt] = useState(false);
  const [lastEvaluatedKeys, SetLastEvaluatedKeys] = useState({
    LastEvaluatedPk: "",
    LastEvaluatedSk: "",
  });
  const [notificationLoader, setNotificationLoader] = useState(false);

  const { userData, isInitialLogin, routesHistory, userAlreadyLogged } = useAppSelector(
    (state: RootState) => state.authReducer
  );
  const [settingsAnchor, setsettingsAnchor] = useState<null | HTMLElement>(
    null
  );
  const { palette, themeMode } = useSelector(
    (state: RootState) => state.themeSlice
  );

  const { userNotifications, userNotificationsAll } = useAppSelector(
    (state: any) => state.userNotificationSlice
  );

  const {
    dashboardInterval,
    summaryInterval,
    detailInterval,
    detailPageAPIInterval,
    relatedSubstanceInterval,
  } = useAppSelector((state) => state.adminSlice);
  const [downloadAppOpen, setDownloadAppOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoSize, setLogoSize] = useState(45);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const isSettingMenuOpen = Boolean(settingsAnchor);
  const [notificationAnchor, setNotificationAnchor] =
    useState<null | HTMLElement>(null);
  const notificationMenuOpen = Boolean(notificationAnchor);

  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const navigate = useNavigate();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleOpenAppDownLoadPopup = () => {
    setDownloadAppOpen(!downloadAppOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleClickSettingsIcon = (event: React.MouseEvent<HTMLElement>) => {
    setsettingsAnchor(event.currentTarget);
  };
  const handleCloseSettingsMenu = () => {
    setsettingsAnchor(null);
  };

  const handleOpenNotificationMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleCloseNotificationMenu = () => {
    setNotificationAnchor(null);
  };

  const updateNotificationReadStatusButNoRedirect = (notificationInfo: any) => {

    let request = {
      update: {
        update: [
          {
            sk: notificationInfo.sk,
            pk: notificationInfo.pk,
            is_read: !notificationInfo.is_read,
          },
        ],
      },
      notificationsAll: userNotificationsAll.notificationsAll,
      notificationsRead: userNotificationsAll.notificationsRead,
      notificationsUnRead: userNotificationsAll.notificationsUnRead,
      count: userNotificationsAll?.count,
      unread: notificationInfo.is_read
        ? userNotificationsAll?.unread + 1
        : userNotificationsAll?.unread - 1,
      read: notificationInfo.is_read
        ? userNotificationsAll?.read - 1
        : userNotificationsAll?.read + 1,
      notificationType: notificationType,
    };
    dispatch(updateNotificationStatusThunk(request)).then(() => {
      if (userData?.group !== 'admin') {
        const request = {
          tenant_id: userData.tenantId,
          username: userData.username,
          event_type: "notification_click",
        }
        dispatch(updateUserHistoryData(request))
      }
    });
  };

  const updateNotificationReadStatus = (notificationInfo: any) => {
    // setNotificationLoader(true);    

    const isRead = notificationInfo?.is_read;
    if (!isRead) {
      let request = {
        update: {
          update: [
            {
              sk: notificationInfo.sk,
              pk: notificationInfo.pk,
              is_read: !notificationInfo.is_read,
            },
          ],
        },
        notificationsAll: userNotificationsAll.notificationsAll,
        notificationsRead: userNotificationsAll.notificationsRead,
        notificationsUnRead: userNotificationsAll.notificationsUnRead,
        count: userNotificationsAll?.count,
        unread: notificationInfo.is_read
          ? userNotificationsAll?.unread + 1
          : userNotificationsAll?.unread - 1,
        read: notificationInfo.is_read
          ? userNotificationsAll?.read - 1
          : userNotificationsAll?.read + 1,
        notificationType: notificationType,
      };
      dispatch(updateNotificationStatusThunk(request));

    }
    if (userData?.group !== 'admin') {
      const request = {
        tenant_id: userData.tenantId,
        username: userData.username,
        event_type: "notification_click",
      }
      setTimeout(() => {
        dispatch(updateUserHistoryData(request))
      }, 1000)
      // dispatch(updateUserHistoryData(request))
    }
    navigate("/redirect-dashboard", {
      state: { notificationData: notificationInfo },
    });
    handleCloseNotificationMenu();
  };

  const updateAllNotificationStatus = () => {
    const request = {
      userName: userData.username,
      list: userNotificationsAll,
      isStatus: true,
    };
    dispatch(updateAllNotificationStatusThunk(request));
  };

  useEffect(() => {
    dispatch(setGlobalLoader(true));
    if (summaryInterval) {
      clearInterval(summaryInterval);
      dispatch(setSummaryInterval(null));
    }
    if (detailInterval) {
      clearInterval(detailInterval);
      dispatch(setDetailInterval(null));
    }
    if (detailPageAPIInterval) {
      clearInterval(detailPageAPIInterval);
      dispatch(setDetailPageAPIInterval(null));
    }
    if (dashboardInterval) {
      clearInterval(dashboardInterval);
      dispatch(setDashboardInterval(null));
    }
    if (relatedSubstanceInterval) {
      clearInterval(relatedSubstanceInterval);
      dispatch(setRelatedSubstanceInterval(null));
    }
    const combinedAllRoutes = [...routesHistory, location?.pathname]?.slice(-2);
    dispatch(setRouteHistory(combinedAllRoutes));
    let request = {
      userToken: userData.signInUserSession.accessToken.jwtToken,
      allUserData: userData,
      previousRoute: combinedAllRoutes,
    };
    dispatch(setInitialLogin(false));
    setTimeout(() => {
      dispatch(getUserData(request));
    }, 1000);
    // dispatch(
    //   sendGAEvent({
    //     category: "Navigation",
    //     action: "Page View",
    //     label: `navigated to ${location.pathname}`,
    //     value: location.pathname,
    //   })
    // );
  }, [location]);

  useEffect(() => {
    if (userData.username) {
      setNotificationLoader(true);
      setIsNormalNotificationCall(true);

      let LastEvaluatedPk = "";
      let LastEvaluatedSk = "";

      if (!__.isEmpty(userNotificationsAll)) {
        if (notificationType === "all") {
          LastEvaluatedPk =
            userNotificationsAll?.notificationsAll?.LastEvaluatedPk;
          LastEvaluatedSk =
            userNotificationsAll?.notificationsAll?.LastEvaluatedSk;
        } else if (notificationType === "read") {
          LastEvaluatedPk =
            userNotificationsAll?.notificationsRead?.LastEvaluatedPk;
          LastEvaluatedSk =
            userNotificationsAll?.notificationsRead?.LastEvaluatedSk;
        } else if (notificationType === "unread") {
          LastEvaluatedPk =
            userNotificationsAll?.notificationsUnRead?.LastEvaluatedPk;
          LastEvaluatedSk =
            userNotificationsAll?.notificationsUnRead?.LastEvaluatedSk;
        }
      }

      dispatch(
        getUserNotificationsWithReadUnreadThunk({
          pageNo: page,
          status: page <= 0 ? "" : notificationType,
          userName: userData.username,
          LastEvaluatedPk: LastEvaluatedPk,
          LastEvaluatedSk: LastEvaluatedSk,
        })
      );

      const interval = setInterval(() => {
        setNotificationLoader(true);
        setIsNormalNotificationCall(false);
        dispatch(
          getUserNotificationsWithReadUnreadThunk({
            pageNo: 0,
            status: "",
            userName: userData.username,
            LastEvaluatedPk: "",
            LastEvaluatedSk: "",
          })
        );
      }, 30000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [userData, page]);

  useEffect(() => {
    // debugger
    let drawerOpen = localStorage.getItem("openDrawer");
    if (drawerOpen === "1") {
      handleDrawerOpen();
      setDrawerOpen(true);
    }
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const setDrawer = (value: string) => {
    localStorage.setItem("openDrawer", value);
    setDrawerOpen(value === "0" ? false : true);
  };

  const handleDrawerSwipeOpen = () => {
    setOpenSwipe(openSwipe ? false : true);
  };

  const handleDrawerSwipeClose = () => {
    setOpenSwipe(false);
  };

  const logOut = () => {
    // dispatch(
    //   sendGAEvent({
    //     category: "Authentication",
    //     action: "Auth-SignOut",
    //     label: userData?.username,
    //     value: userData?.username,
    //   })
    // );
    Auth.signOut()
    dispatch(stopTracking());
    // dispatch(startTracking(location.pathname));
    setTimeout(() => {
      const rememberMe = localStorage.getItem('rememberMe');
      localStorage.clear();
      if (rememberMe) {
        localStorage.setItem('rememberMe', rememberMe);
      }
      window.location.href = "/login";
    }, 1000)
  };

  const privacyPolicy = () => { };
  const termsCondition = () => { };

  const handleThemeChange = (event: any) => {
    themeMode === "dark"
      ? dispatch(manageTheme("light"))
      : dispatch(manageTheme("dark"));
  };

  const menuId = "primary-search-account-menu";

  const mobileMenuId = "primary-search-account-menu-mobile";

  useEffect(() => {
    let menus: any = [];
    menuItems.map((menu: any) => {
      if (
        JSON.parse(
          userData.permission !== null ? userData.permission : "[]"
        ).includes(menu.menuId) ||
        userData.group == "admin"
      )
        menus.push(menu);
      return menu;
    });
    const menuSubMenuList: any = menus
      .map((x: any) => {
        const mainArr = [x.to];
        if (x?.subMenu?.length) {
          mainArr.push(...x?.subMenu?.map((y: any) => y.to));
        }
        return mainArr;
      })
      .flat(2);
    const menuListWithPostfixSlash = menuSubMenuList.map((x: any) => {
      return x + "/";
    });
    const masterMenu: any = [...menuSubMenuList, ...menuListWithPostfixSlash];
    if (isInitialLogin) {
      navigate(menus?.[0]?.to || "");
    } else if (location.pathname == "/") {
      navigate(menus?.[0]?.to || "");
    } else if (
      !masterMenu.find((x: any) => x == location.pathname) &&
      // !routeWhichIsNotInMenu.includes(location.pathname)
      !isRouteInList(location.pathname, routeWhichIsNotInMenu)
    ) {
      navigate(menus?.[0]?.to || "");
    }
    setMenuList(menus);
  }, [userData]);

  const redirectToProfile = () => {
    navigate("/profile");
    handleMenuClose();
  };

  const redirectToNotifications = () => {
    navigate("/notifications");
    handleMenuClose();
  };

  useEffect(() => {
    setNotificationLoader(false);
  }, [userNotificationsAll]);

  const handleCloseContactSupport = useMemo(() => {
    const handleClickFunction = () => {
      setContactOpen(false);
    };
    return handleClickFunction;
  }, [contactOpen]);
  const handleClickContactSupportOpen = useMemo(() => {
    const handleClickFunction = () => {
      setContactOpen(true);
    };
    return handleClickFunction;
  }, [contactOpen]);

  const handleCloseTermsCondition = useMemo(() => {
    const handleClickFunction = () => {
      setTermsConditionOpen(false);
    };
    return handleClickFunction;
  }, [termsConditionOpen]);

  const handleClickTermsConditionOpen = useMemo(() => {
    const handleClickFunction = () => {
      setTermsConditionOpen(true);
    };
    return handleClickFunction;
  }, [termsConditionOpen]);

  const handleClosePrivacyPolicy = useMemo(() => {
    const handleClickFunction = () => {
      setPrivacyPolicyOpen(false);
    };
    return handleClickFunction;
  }, [termsConditionOpen]);

  const handleClickPrivacyPolicyOpen = useMemo(() => {
    const handleClickFunction = () => {
      setPrivacyPolicyOpen(true);
    };
    return handleClickFunction;
  }, [termsConditionOpen]);

  const handleClickContactSupportSubmit = useMemo(() => {
    const handleClickFunction = (data: any) => {
      setLoader(true);
      dispatch(sendContactSupportThunk({ request: data })).then(() => {
        setLoader(false);
        handleCloseContactSupport();
      });
    };
    return handleClickFunction;
  }, [contactOpen]);

  const toggleDrawer = () => {
    setDownloadAppOpen(false);
  };

  const redirectToScheduleReports = () => {
    navigate("/schedule-reports");
    handleMenuClose();
  };

  useEffect(() => {
    const updateSize = () => setLogoSize(window.innerWidth > 600 ? 50 : 30);
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const showIeqGptContent = (userData?.group === 'admin' || (userData?.permission?.includes('34') && (userData.isActive === "true")))    
  console.log("showIeqGptContent",showIeqGptContent, userData?.permission, userData.isActive);
  
  if(props.hidden){
    return (<></>)
  }
 
  return (
    <Box
      sx={{ ml: drawerOpen ? { xs: 0, md: 28 } : { xs: 0, md: 8 } }}
      className="main-container"
    >
      <SnackBar />
      <CssBaseline />
      <AppBar position="fixed" sx={{ /* zIndex: 9999 */ }}>
        <Toolbar sx={{ ml: 0 }}>
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={handleDrawerSwipeOpen}
            edge="start"
            sx={{
              display: { xs: "block", md: "none" },
              ml: 2,
              // color: "#fff",
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            component="h2"
            variant="h4"
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#fff",
              ml: { xs: 0, sm: 2 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="img"
                src="/assets/images/logo.png"
                alt="Company Logo"
                onError={(e) => (e.currentTarget.src = "/assets/images/logo.png")}
                sx={{
                  height: logoSize,
                  mt: "5px",
                  mb: "5px",
                }}
              />
            </Box>
          </Typography>
          <Box sx={{ display: "flex", ml: "auto" }}>
            {(showIeqGptContent && (userData?.group === 'admin' || userData?.tenantCode === 'cyunsqrn')) &&
              <Tooltip title={"IEQGPT"}>
                <IconButton
                  size="large"
                  edge="end"
                  aria-controls={menuId}
                  aria-haspopup="true"
                  sx={{ mr: 1 }}
                  onClick={() => {
                    navigate("/ieq-gpt")
                    // setShowIeqGpt(prev => !prev)
                  }}
                >
                  <QuestionAnswerIcon />
                </IconButton>
              </Tooltip>
            }

            {/* <Tooltip title={"Download Mobile App"}>
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                sx={{ color: "#fff", mr: 1 }}
                onClick={handleOpenAppDownLoadPopup}
              >
                <SmartphoneIcon />
              </IconButton>
            </Tooltip> */}

            {/* <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleClickSettingsIcon}
              sx={{ color: "#fff", mr: 1 }}
            >
              <GoGear />
            </IconButton> */}
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              sx={{ mr: 1 }}
              onClick={handleOpenNotificationMenu}
            >
              {true ? (
                <Badge
                  badgeContent={userNotificationsAll?.unread}
                  color="error"
                >
                  <BiBell />
                </Badge>
              ) : (
                <BiBell />
              )}
            </IconButton>
            <Box sx={{ display: { xs: "none", sm: "flex" } }}>

              <IconButton
                size="medium"
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
              // sx={{ color: "#fff" }}
              >
                {userData?.profile ? (
                  <>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        border: '2px solid #fff',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.1)',
                        },
                      }}
                      src={userData?.profile}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', paddingX: { xs: "0px", sm: '0.5rem' } }}>
                      <Typography
                        component="span"
                        variant="h6"
                        sx={{
                          // color: "#FFFFFF",
                          paddingLeft: "6px",
                          fontSize: "0.9rem",
                          fontWeight: 600
                        }}
                      >
                        {userData?.name}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          // color: "#FFFFFF",
                          paddingLeft: "6px",
                          fontSize: "0.7rem",
                          display: 'block',
                          fontWeight: 600
                        }}
                      >
                        {userData?.timezone || ''}
                      </Typography>
                    </Box>
                    <ArrowDropDownIcon />
                  </>
                ) : (
                  <>
                    <AccountCircle />
                    <Box sx={{ display: 'flex', flexDirection: 'column', paddingX: '0.5rem' }}>
                      <Typography
                        component="span"
                        variant="h6"
                        sx={{
                          // color: "#FFFFFF",
                          paddingLeft: "6px",
                          fontSize: "0.9rem",
                        }}
                      >
                        {userData?.attributes?.name}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          // color: "#FFFFFF",
                          paddingLeft: "6px",
                          fontSize: "0.7rem",
                          display: 'block'
                        }}
                      >
                        {userData?.timezone || ''}
                      </Typography>
                    </Box>
                    <ArrowDropDownIcon />
                  </>
                )}
              </IconButton>
            </Box>
            <Box sx={{ display: { xs: "flex", sm: "none" }, ml: "auto" }}>
              <IconButton
                size="large"
                aria-label="show more"
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
              // sx={{ color: "#fff" }}
              >
                <MoreIcon />
              </IconButton>
            </Box>
          </Box>

        </Toolbar>
      </AppBar>
      <DownloadDrawer
        anchor={"right"}
        variant={downloadAppOpen ? "permanent" : "persistent"}
        open={downloadAppOpen}
        onClose={toggleDrawer}
        PaperProps={{
          style: {
            background: themeMode === "dark" ? "#171bf1 !important" : "#e9e9e9",
          },
        }}
      >
        <DownloadAppComponent toggleDrawer={toggleDrawer} />
      </DownloadDrawer>
      {
        ((userData.isActive === "true" && ssoenabled) ||
          userData.group === "admin") && (
          <Drawer
            variant="permanent"
            open={open}
            sx={{
              display: { xs: "none", md: "block" },
              zIndex: 9999,
              "& .MuiPaper-root": {
                borderRadius: 0, // Adjust as needed
                borderRight: 'linear-gradient(180deg, #ffffff 0%,rgb(180, 195, 241) 25%, #77c4f8 90%)'
              },
            }}
            className={`${themeMode === "dark" ? "dark-bg" : ""} hideScrollbar`}
            onMouseEnter={handleDrawerOpen}
            onMouseLeave={!drawerOpen ? handleDrawerClose : () => { }}
            PaperProps={{
              style: {
                background:
                  themeMode === "dark" ? "#171bf1 !important" : "#ffffff",
              },
            }}
          >
            <List
              className={"hideScrollbar"}
              sx={{
                width: "auto",
                height: "85vh",
                mt: 7.5,
                overflowY: "auto",
                overflowX: "hidden",
                mb: 5,
                padding: "10px",
                gap: 5
              }}
            >
              {menuList.map((row: any, index: number) =>
                row.subMenu != undefined && row.subMenu.length > 0 ? (
                  <SidebarItemCollapse
                    item={row}
                    index={index}
                    key={index}
                    open={open}
                  />
                ) : (
                  <SidebarItem item={row} index={index} key={index} open={open} />
                )
              )}

              <ListItem
                disablePadding
                className="sidebar-drawer-button"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  ml: open ? 1 : 0,
                  borderRadius: "3px 3px 3px 3px",
                  width: open ? "205px !important" : "60px !important",
                  // height: "40px",
                  // position: "absolute",
                  // bottom: "0px",
                  background: palette.primary,
                  // marginBottom: "-40px",
                }}
              >
                {drawerOpen ? (
                  <ListItemButton
                    disableRipple
                    onClick={() => {
                      handleDrawerClose();
                      setDrawer("0");
                    }}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        // mr: open ? 3 : "auto",
                        justifyContent: "center",
                        color: "#fff",
                      }}
                    >
                      <ChevronLeftIcon />
                    </ListItemIcon>
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    className="sidebar-drawer-button"
                    disableRipple
                    onClick={() => {
                      handleDrawerOpen();
                      setDrawer("1");
                    }}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        width: open ? "179px !important" : "32px !important",
                        // mr: open ? 3 : "auto",
                        justifyContent: "center",
                        color: "#fff",
                      }}
                    >
                      <KeyboardDoubleArrowRightIcon />
                    </ListItemIcon>
                  </ListItemButton>
                )}
              </ListItem>
            </List>
          </Drawer>
        )
      }
      {
        ((userData.isActive === "true" && ssoenabled) ||
          userData.group === "admin") && (
          <SwipeableDrawer
            anchor={"left"}
            open={openSwipe}
            onClose={handleDrawerSwipeClose}
            onOpen={handleDrawerSwipeOpen}
            sx={{
              display: { xs: "block", md: "none" },
              zIndex: "9999"
            }}
          >
            <DrawerHeader sx={{ minHeight: "48px !important" }}>
              <Box component={"span"} >
                <img
                  src={"/assets/images/logo.png"}
                  height={30}
                  style={{
                    marginTop: "5px",
                    marginBottom: "5px",
                    marginRight: "5px",
                  }}
                  title="ieqMax"
                />
              </Box>
              <IconButton onClick={handleDrawerSwipeClose}>
                {theme.direction === "rtl" ? (
                  <ChevronRightIcon />
                ) : (
                  <ChevronLeftIcon />
                )}
              </IconButton>
            </DrawerHeader>
            <Divider />
            <List sx={{ background: themeMode === "dark" ? "#171b1f" : "", paddingTop: '1rem' }}>
              {menuList.map((row: any, index: number) =>
                row.subMenu != undefined && row.subMenu.length > 0 ? (
                  <SidebarItemCollapse
                    key={index}
                    item={row}
                    index={index}
                    open={true}
                    handleDrawerSwipeClose={handleDrawerSwipeClose}
                  />
                ) : (
                  <SidebarItem
                    key={index}
                    item={row}
                    index={index}
                    open={true}
                    handleDrawerSwipeClose={handleDrawerSwipeClose}
                  />
                )
              )}
            </List>
          </SwipeableDrawer>
        )
      }
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: "72px",
          background: themeMode === "dark" ? "#080a0c !important" : 'linear-gradient(180deg, #E7EAEE 0%, #F4F5F8 25%, #FFFFFF 90%)',
        }}
      >
        {props.children}
      </Box>
      <Menu
        anchorEl={anchorEl}
        id={menuId}
        keepMounted
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={redirectToProfile}>
          <AccountCircle sx={{ mr: 2 }} />
          My Profile
        </MenuItem>
        {(userData.group === "admin" || userData.group === "tenantAdmin") && (
          <MenuItem onClick={redirectToNotifications}>
            <NotificationsActiveIcon sx={{ mr: 2 }} />
            Manage Notifications
          </MenuItem>
        )}
        {(userData?.group === 'admin' || userData?.permission?.includes('25')) && <MenuItem onClick={redirectToScheduleReports} className="ga-reports">
          <TimerIcon sx={{ mr: 2 }} />
          Scheduled Reports
        </MenuItem>}
        {/* <MenuItem component="a" href="http://saasstack-websitebucket75c24d94-y7v58mtpy838.s3-website-us-east-1.amazonaws.com/privacy.pdf" target="_blank" className="ga-logout">
          <PolicyIcon sx={{ mr: 2 }} /> Privacy Policy
        </MenuItem> */}
        <MenuItem onClick={handleClickPrivacyPolicyOpen} className="ga-logout">
          <PolicyIcon sx={{ mr: 2 }} /> Privacy Policy
        </MenuItem>
        {/* <MenuItem component="a" href="http://saasstack-websitebucket75c24d94-y7v58mtpy838.s3-website-us-east-1.amazonaws.com/TOS.pdf" target="_blank" className="ga-logout">
          <PrivacyTipIcon sx={{ mr: 2 }} />Terms & Condition
        </MenuItem> */}
        <MenuItem onClick={handleClickTermsConditionOpen} className="ga-logout">
          <PrivacyTipIcon sx={{ mr: 2 }} />
          Terms & Condition
        </MenuItem>
        <MenuItem onClick={handleClickContactSupportOpen} className="ga-logout">
          <ContactMailIcon sx={{ mr: 2 }} />
          Contacts Support
        </MenuItem>
        <MenuItem onClick={logOut} className="ga-logout">
          <LogoutIcon sx={{ mr: 2 }} /> Logout
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={mobileMoreAnchorEl}
        id={mobileMenuId}
        keepMounted
        open={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
      >
        <MenuItem onClick={redirectToProfile}>
          <AccountCircle sx={{ mr: 2 }} /> Profile
        </MenuItem>
        {(userData.group === "admin" || userData.group === "tenantAdmin") && (
          <MenuItem onClick={redirectToNotifications}>
            <NotificationsActiveIcon sx={{ mr: 2 }} />
            Manage Notifications
          </MenuItem>
        )}
        {(userData?.group === 'admin' || userData?.permission?.includes('25')) && <MenuItem onClick={redirectToScheduleReports} className="ga-reports">
          <TimerIcon sx={{ mr: 2 }} />
          Scheduled Reports
        </MenuItem>}
        {/* <MenuItem onClick={handleOpenNotificationMenu}>
          <NotificationsIcon sx={{ mr: 2 }} /> Notifications
        </MenuItem> */}
        {/* <MenuItem component="a" href="http://saasstack-websitebucket75c24d94-y7v58mtpy838.s3-website-us-east-1.amazonaws.com/privacy.pdf" target="_blank" className="ga-logout">
          <PolicyIcon sx={{ mr: 2 }} />Privacy Policy
        </MenuItem>
        <MenuItem component="a" href="http://saasstack-websitebucket75c24d94-y7v58mtpy838.s3-website-us-east-1.amazonaws.com/TOS.pdf" target="_blank" className="ga-logout">
          <PrivacyTipIcon sx={{ mr: 2 }} />Terms & Condition
        </MenuItem> */}
        <MenuItem onClick={handleClickPrivacyPolicyOpen} className="ga-logout">
          <PolicyIcon sx={{ mr: 2 }} /> Privacy Policy
        </MenuItem>
        <MenuItem onClick={handleClickTermsConditionOpen} className="ga-logout">
          <PrivacyTipIcon sx={{ mr: 2 }} />
          Terms & Condition
        </MenuItem>
        <MenuItem onClick={handleClickContactSupportOpen}>
          <ContactMailIcon sx={{ mr: 2 }} />
          Contacts Support
        </MenuItem>
        {/* <MenuItem onClick={handleClickSettingsIcon}>
          <SettingsIcon sx={{ mr: 2 }} />Settings
        </MenuItem> */}
        <MenuItem onClick={logOut}>
          <LogoutIcon sx={{ mr: 2 }} />
          Logout
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={settingsAnchor}
        id="account-menu"
        open={isSettingMenuOpen}
        onClose={handleCloseSettingsMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem>
          <Box>
            <ThemeSwitch
              onClick={handleThemeChange}
              checked={themeMode === "dark"}
            />
          </Box>
        </MenuItem>
      </Menu>
      <Menu
        className="notificationsAllItems"
        anchorEl={notificationAnchor}
        id="account-menu"
        open={notificationMenuOpen}
        onClose={handleCloseNotificationMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "0px",
            padding: "0px",
            width: { xs: "100%", md: "44%" },
            overflowY: "hidden",
            position: "fixed",
            // overflowY:
            //   userNotifications?.notifications?.length > 0 &&
            //   !notificationLoader
            //     ? "scroll"
            //     : "hidden",
            overflowX: "hidden",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              // position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translate(-50%,-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <NotificationItems
          notificationLoader={notificationLoader}
          userNotificationsAll={userNotificationsAll}
          updateAllNotificationStatus={updateAllNotificationStatus}
          updateNotificationReadStatus={updateNotificationReadStatus}
          updateNotificationReadStatusButNoRedirect={
            updateNotificationReadStatusButNoRedirect
          }
          themeMode={themeMode}
          page={page}
          setPage={setPage}
          isNormalNotificationCall={isNormalNotificationCall}
          notificationType={notificationType}
          setNotificationType={setNotificationType}
        />
      </Menu>
      <ContactsSupport
        loader={loader}
        open={contactOpen}
        handleClose={handleCloseContactSupport}
        handleClickUpdate={handleClickContactSupportSubmit}
        userData={userData}
      />
      <TermsAndCondition
        open={termsConditionOpen}
        handleClose={handleCloseTermsCondition}
      />
      <PrivacyPolicy
        open={privacyPolicyOpen}
        handleClose={handleClosePrivacyPolicy}
      />
      {/* IEQ GPT */}
      {/* {(showIeqGptContent) && <SwipeableDrawer
        sx={{ zIndex: 11001, width: "100%" }}
        anchor={'right'}
        open={showIeqGpt}
        onClose={() => setShowIeqGpt(prev => !prev)}
        onOpen={() => setShowIeqGpt(prev => !prev)}
      >
        <IEQ setShowIeqGpt={setShowIeqGpt} />
      </SwipeableDrawer>} */}
    </Box >
  );
}
